<script setup lang="ts">
import type AntiRobot from '~/components/base/form/AntiRobot.vue';
import type StrengthBar from '~/components/auth/StrengthBar.vue';

const { $kyc } = useNuxtApp();
const { t } = useTexts();

const { openToaster } = useLayoutStore();
const { isAuthenticated, recoveryPasswordFlow } = storeToRefs(useAuthStore());
const { resetRecoveryPasswordFlow, recoveryPasswordData } = useAuthStore();
const { validationData } = storeToRefs(useRegisterStore());
const { $reset } = useRegisterStore();
const { isCaptchaResetPasswordEnabled, isInvisibleCaptcha } = useCaptcha();

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);

const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const captchaComponentClass = computed<string>(() => {
  if (!isCaptchaResetPasswordEnabled.value) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (isInvisibleCaptcha.value) {
    return 'mt-3 mb-6 relative max-w-[150px]';
  }

  return 'mt-5 mb-0 max-w-[320px]';
});

const token = computed(() => {
  return recoveryPasswordFlow.value?.token ?? '';
});

const strengthBarRef = ref<InstanceType<typeof StrengthBar> | null>(null);
const fieldNewPassword = ref('');
const fieldConfirmPassword = ref('');

const isLoading = ref(false);
const isFormVisible = ref(true);

const isFormValid = computed(() => {
  if (isCaptchaResetPasswordEnabled.value && !antiRobotToken.value) {
    return false;
  }

  return (
    validationData.value.fields.password &&
    fieldNewPassword.value === fieldConfirmPassword.value
  );
});

async function sendForm() {
  if (!token.value) {
    openToaster({
      message: t('auth.reset_password.error.token'),
      type: 'error',
    });

    return;
  }

  if (!fieldNewPassword.value) {
    openToaster({
      message: t('auth.reset_password.error.new_password'),
      type: 'error',
    });

    return;
  }

  if (!fieldConfirmPassword.value) {
    openToaster({
      message: t('auth.reset_password.error.password_confirmation'),
      type: 'error',
    });

    return;
  }

  if (fieldNewPassword.value !== fieldConfirmPassword.value) {
    openToaster({
      message: t('auth.change_password.error.password_confirmation'),
      type: 'error',
    });

    return;
  }

  if (
    fieldNewPassword.value.length < 8 ||
    fieldConfirmPassword.value.length < 8
  ) {
    openToaster({
      message: t('auth.reset_password.error.min_length'),
      type: 'error',
    });

    return;
  }

  if (!validationData.value.fields.password) {
    openToaster({
      message: t('auth.change_password.error.password_confirmation'),
      type: 'error',
    });

    return;
  }

  if (isCaptchaResetPasswordEnabled.value && !antiRobotToken.value) {
    openToaster({
      message: t('auth.login.verify_captcha'),
      type: 'warning',
    });

    return;
  }

  const submitData: IResetPassword = {
    token: token.value,
    password: fieldNewPassword.value,
    password_confirmation: fieldConfirmPassword.value,
    captcha_token: antiRobotToken.value,
  };

  if (recoveryPasswordData?.code) {
    submitData.code = recoveryPasswordData?.code;
  }

  if (
    recoveryPasswordData?.kyc_id &&
    $kyc.current.source !== 'password_recovery_attempt_limit'
  ) {
    submitData.kyc_id = recoveryPasswordData?.kyc_id;
  }

  isLoading.value = true;

  const { ApiService } = useApiService();

  await ApiService.post('/auth/passwords/reset/confirm', submitData)
    .then((response) => {
      isLoading.value = false;

      if (response.status.toString().startsWith('20')) {
        isFormVisible.value = false;
        resetRecoveryPasswordFlow();
      } else {
        const message =
          response.message ??
          response.data.message ??
          t('auth.login.error.reset_password');

        openToaster({
          message,
          type: 'error',
        });
      }
    })
    .catch((err) => {
      const errorMessage =
        err?.data && err.data?.message
          ? err.data.message
          : t('auth.change_password.error.new_password_invalid');

      openToaster({
        message: errorMessage,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function updatePasswordStrength() {
  strengthBarRef.value?.updatePasswordStrength();
}

onUnmounted(() => {
  $reset();
});
</script>

<template>
  <div :class="$style.recoverPageWrapper">
    <UserBox
      v-if="isFormVisible"
      class="w-full"
      :title="$t('account.recover_pasword')"
    >
      <span class="text-xs mb-4 block">
        {{ $t('auth.recover_password.description') }}
      </span>

      <form
        v-if="!isAuthenticated"
        :class="$style.formContainer"
        @submit.prevent="sendForm"
      >
        <BaseLoading
          class="!bg-auth-bg-primary"
          :icon-loader="true"
          :show="isLoading"
        />

        <div :class="$style.formGroup">
          <label for="newPassword">
            {{ $t('account.security.new_password') }}<span>*</span>
          </label>
          <BaseFormInput
            id="newPassword"
            v-model="fieldNewPassword"
            name="newPassword"
            :placeholder="$t('account.security.new_password')"
            :required="true"
            :show-hide-pass="true"
            @keyup="updatePasswordStrength"
          />
        </div>

        <div :class="$style.formGroup">
          <label for="newPassword">
            {{ $t('account.security.confirm_password') }}<span>*</span>
          </label>
          <BaseFormInput
            id="confirmPassword"
            v-model="fieldConfirmPassword"
            name="confirmPassword"
            :placeholder="$t('account.security.confirm_password')"
            :required="true"
            :show-hide-pass="true"
          />
        </div>

        <div class="pt-3 pb-1 flex flex-col gap-5">
          <AuthStrengthBar
            ref="strengthBarRef"
            :password="fieldNewPassword"
          />
        </div>

        <div class="flex justify-between items-end w-full col-span-2 mt-4">
          <button
            :class="$style.btnSubmit"
            :disabled="!isFormValid"
            type="submit"
          >
            <NuxtIcon name="check" />
            {{ $t('auth.change_password.send_btn') }}
          </button>
        </div>

        <div :class="captchaComponentClass">
          <BaseFormAntiRobot ref="antiRobotRef" />
        </div>
      </form>

      <div
        v-else
        class="mt-4 alert alert-warning"
      >
        {{ $t('error.unauthorized.message') }}
      </div>
    </UserBox>
    <AuthPasswordResetSuccess v-else />
  </div>
</template>

<style module lang="scss">
.recoverPageWrapper {
  @apply mb-5 md:mb-8 flex items-start justify-center gap-4 w-full;

  .formContainer {
    .formGroup {
      @apply mb-2;

      label {
        @apply text-xs font-medium;
        span {
          @apply text-error;
        }
      }
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed;
  }
}
</style>
